import { Action, handleActions } from 'redux-actions'
import { ActivityDto, Route } from '../../../services/api.types'
import {
  getUserActivities,
  getUserActivitiesFail,
  getUserActivitiesSuccess,
  loadMoreActivities,
  loadMoreActivitiesSuccess,
  loadMoreActivitiesFail,
  loadActivityRoute,
  loadActivityRouteSuccess,
  loadActivityRouteFail,
  deleteActivity,
  deleteActivitySuccess,
  deleteActivityFail,
  openReviewDuplicates,
  closeReviewDuplicates,
  loadDuplicatesSuccess,
  loadDuplicatesFail,
  setMainActivity,
  setMainActivitySuccess,
  setMainActivityFail,
} from './ActivitiesActions'

interface DuplicatesResult {
  activities: ActivityDto[]
  mainActivityId: string
}

interface SetMainResult extends DuplicatesResult {
  previousMainId: string
}

// Page size requested per fetch. There are more activities to load only when a fetch returns a
// full page — a shorter page means we've reached the end. Must match the `size` used in the
// Activities view requests.
const PAGE_SIZE = 20

class State {
  list: ActivityDto[] = []
  isLoading = false
  // Whether at least one fetch has completed. The view must not show the "no activities" empty
  // state before the first response arrives — the slice is not persisted, so every reload starts
  // with an empty list.
  hasLoaded = false
  isLoadingMore = false // New state for loading more activities
  hasMore = true // New state to track if there are more activities to load
  offset = 0 // New state to track current offset for pagination

  // Lazily loaded GPS routes, keyed by activity id (covers multi-sport legs too). Kept outside
  // the list items so route arrival doesn't churn the whole list.
  routesById: { [activityId: string]: Route[] } = {}
  routeLoadingIds: { [activityId: string]: boolean } = {}

  // Review duplicates modal
  reviewModalOpen = false
  reviewMainId: string | null = null // id of the main activity of the group under review
  duplicates: ActivityDto[] = [] // the full duplicate group (main + duplicates)
  duplicatesLoading = false
  settingMain = false
}

export default handleActions<State, any>(
  {
    [String(getUserActivities)]: (state: State): State => ({
      ...state,
      isLoading: true,
      // Reset pagination when starting fresh
      offset: 0,
      hasMore: true,
      // Also clear the load-more flag: a stale `true` (e.g. rehydrated from a session that ended
      // mid-request) would permanently block every future load-more.
      isLoadingMore: false,
    }),
    [String(getUserActivitiesSuccess)]: (state: State, action: Action<ActivityDto[]>) => {
      const incoming = action.payload || []
      // Defensively drop any duplicate ids within the page so the list never has duplicate keys.
      const seen = new Set<string>()
      const list = incoming.filter((activity) => (seen.has(activity.id) ? false : seen.add(activity.id)))
      return {
        ...state,
        list,
        isLoading: false,
        hasLoaded: true,
        offset: list.length,
        // Only a full page implies there may be more; a shorter page means we've reached the end.
        hasMore: incoming.length >= PAGE_SIZE,
        // Fresh list — drop the route cache so re-synced/enriched routes are refetched.
        routesById: {},
        routeLoadingIds: {},
      }
    },
    [String(getUserActivitiesFail)]: (state: State): State => ({
      ...state,
      isLoading: false,
      hasLoaded: true,
    }),
    // New actions for infinite scroll
    [String(loadMoreActivities)]: (state: State): State => ({
      ...state,
      isLoadingMore: true,
    }),
    [String(loadMoreActivitiesSuccess)]: (state: State, action: Action<ActivityDto[]>) => {
      const incoming = action.payload || []
      // Drop any items already in the list. A filter change resets the offset while a
      // scroll-triggered load-more may still be in flight, so the same page can arrive twice —
      // appending it blindly produces duplicate React keys and breaks list reconciliation.
      const existingIds = new Set(state.list.map((activity) => activity.id))
      const newItems = incoming.filter((activity) => !existingIds.has(activity.id))
      const list = [...state.list, ...newItems]
      return {
        ...state,
        list,
        isLoadingMore: false,
        // Next fetch starts after everything we actually hold (kept consistent after de-duping).
        offset: list.length,
        // "Has more" is based on the raw page the server returned, not the de-duped count.
        hasMore: incoming.length >= PAGE_SIZE,
      }
    },
    [String(loadMoreActivitiesFail)]: (state: State): State => ({
      ...state,
      isLoadingMore: false,
    }),
    // Lazy route loading
    [String(loadActivityRoute)]: (state: State, action: Action<{ activityId: string }>): State => ({
      ...state,
      routeLoadingIds: { ...(state.routeLoadingIds || {}), [action.payload.activityId]: true },
    }),
    [String(loadActivityRouteSuccess)]: (state: State, action: Action<{ activityId: string; routes: Route[] }>) => {
      const { activityId, routes } = action.payload
      const routeLoadingIds = { ...(state.routeLoadingIds || {}) }
      delete routeLoadingIds[activityId]
      return {
        ...state,
        routesById: { ...(state.routesById || {}), [activityId]: routes },
        routeLoadingIds,
      }
    },
    [String(loadActivityRouteFail)]: (state: State, action: Action<{ activityId: string }>) => {
      const routeLoadingIds = { ...(state.routeLoadingIds || {}) }
      delete routeLoadingIds[action.payload.activityId]
      return { ...state, routeLoadingIds }
    },
    // Delete activity actions
    [String(deleteActivity)]: (state: State): State => ({
      ...state,
      // Keep existing state, deletion is handled optimistically
    }),
    [String(deleteActivitySuccess)]: (state: State, action: Action<string>) => {
      const deletedId = action.payload
      // Remove from the main list and, if a duplicate under review was deleted, keep the main's
      // duplicateCount in sync so the "Review duplicates" link reflects the smaller group.
      const list = state.list
        .filter((activity) => activity.id !== deletedId)
        .map((activity) => {
          let next = activity
          // If a multi-sport leg was deleted, drop it from its parent's nested legs (the parent's
          // combined totals are derived from these, so they update too).
          if (next.childActivities?.some((leg) => leg.id === deletedId)) {
            next = { ...next, childActivities: next.childActivities.filter((leg) => leg.id !== deletedId) }
          }
          // If a duplicate under review was deleted, keep the main's duplicateCount in sync so the
          // "Review duplicates" link reflects the smaller group.
          if (next.id === state.reviewMainId && next.duplicateCount) {
            next = { ...next, duplicateCount: next.duplicateCount - 1 }
          }
          return next
        })
      const duplicates = state.duplicates.filter((activity) => activity.id !== deletedId)
      // Once only the main remains there is nothing left to review — close the modal.
      const collapsed = state.reviewModalOpen && duplicates.length <= 1
      return {
        ...state,
        list,
        duplicates: collapsed ? [] : duplicates,
        reviewModalOpen: collapsed ? false : state.reviewModalOpen,
        reviewMainId: collapsed ? null : state.reviewMainId,
      }
    },
    [String(deleteActivityFail)]: (state: State): State => ({
      ...state,
      // Keep existing state, error handling is done via notifications
    }),
    // Review duplicates modal
    [String(openReviewDuplicates)]: (state: State): State => ({
      ...state,
      reviewModalOpen: true,
      duplicatesLoading: true,
      duplicates: [],
      reviewMainId: null,
    }),
    [String(closeReviewDuplicates)]: (state: State): State => ({
      ...state,
      reviewModalOpen: false,
      duplicatesLoading: false,
      settingMain: false,
      duplicates: [],
      reviewMainId: null,
    }),
    [String(loadDuplicatesSuccess)]: (state: State, action: Action<DuplicatesResult>) => ({
      ...state,
      duplicates: action.payload.activities,
      reviewMainId: action.payload.mainActivityId,
      duplicatesLoading: false,
    }),
    [String(loadDuplicatesFail)]: (state: State): State => ({
      ...state,
      // Nothing to show — close the sheet; the saga surfaces a warning notification.
      duplicatesLoading: false,
      reviewModalOpen: false,
      reviewMainId: null,
    }),
    [String(setMainActivity)]: (state: State): State => ({
      ...state,
      settingMain: true,
    }),
    [String(setMainActivitySuccess)]: (state: State, action: Action<SetMainResult>) => {
      const { activities, mainActivityId, previousMainId } = action.payload
      const newMain = activities.find((activity) => activity.id === mainActivityId)
      // Swap the previous main out of the list for the newly chosen one, preserving scroll position.
      const list = newMain
        ? state.list.map((activity) =>
            activity.id === previousMainId ? { ...newMain, duplicateCount: activities.length - 1 } : activity,
          )
        : state.list
      return {
        ...state,
        settingMain: false,
        // Choice applied — close the modal and reset its state.
        reviewModalOpen: false,
        duplicates: [],
        reviewMainId: null,
        list,
      }
    },
    [String(setMainActivityFail)]: (state: State): State => ({
      ...state,
      settingMain: false,
    }),
  },
  new State(),
)
